<template>
  <ChartsSkeleton v-if="isLoadingState" />
  <component
    :is="wrapperComponent"
    v-else
    :chart-id="chartId"
    :chart-size="chartSize"
    :read-only="readOnly"
    :data-text="dataText"
    :title="title"
    :tooltip-text="tooltipText"
    @change-chart-size="
      (payload: ChartSizeChangePayload) => emit('update-chart-layout', payload)
    "
    @download-chart-data="downloadChartData"
    @download-chart-image="downloadChartImage"
    @open-edit-dialog="toggleEditChart"
    @remove-chart="removeChart"
  >
    <div class="h-full">
      <div
        class="rounded-b-xl h-full"
        :class="{ 'rounded-t-xl': wrapperComponent === 'div' }"
      >
        <highcharts
          ref="myChart"
          :key="chartKey"
          :options="chartOptions"
          :update-args="[true]"
          style="height: calc(100% - 2rem)"
        ></highcharts>
      </div>
    </div>
    <ChartEditor
      v-if="showChartEditor"
      :chart-id="chartId"
      :chart-size="chartSize"
      :initial-series="seriesData"
      :initial-metric="{
        id: 0,
        key: props.metric,
      }"
      :initial-slice-by="{
        id: 0,
        key: props.sliceBy,
      }"
      :chart-type="type"
      :chart-title="title"
      :chart-tooltip="tooltipText"
      @add="handleEdit"
      @cancel="toggleEditChart"
    />
  </component>
</template>

<script setup lang="ts">
import { ref, computed, PropType, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useNuxtApp } from "nuxt/app";
import { useSearchStore } from "~/store/search";
import { useChartsStore } from "~/store/charts.ts";
import { convertCurrency } from "~/utils/ConvertCurrency";
import ChartWrapper from "~/components/charts/ChartWrapper.vue";
import ChartAnalyticsWrapper from "~/components/charts/ChartAnalyticsWrapper.vue";
import ChartEditor from "~/components/dialogs/ChartEditor.vue";
import ChartsSkeleton from "~/components/charts/ChartsSkeleton.vue";
import { processSeries } from "~/utils/ChartBuilderUtils/index";
import {
  ChartSizeChangePayload,
  ChartType,
  ChartMetric,
  ChartSliceBy,
  AddChartPayload,
} from "~/types/ChartsType";
import { useChartOperations } from "~/composables/useChartOperations";
import Emitter from "~/types/Emitter";
import { translateSeriesNames } from "~/utils/translateChartSeries";

const { $bus }: Emitter = useNuxtApp();
const { t } = useI18n();

const savedSearchStore = useSearchStore();
const chartsStore = useChartsStore();
const myChart = ref(null);
const showChartEditor = ref(false);
const { removeAnalyticsChart } = useChartOperations();
const chartKey = ref(0);

const emit = defineEmits([
  "update-chart-layout",
  "updateSearchText",
  "chartLoaded",
  "handleEdit",
]);

const props = defineProps({
  type: {
    type: String as PropType<ChartType>,
    required: true,
  },
  sliceBy: {
    type: String as PropType<ChartSliceBy>,
    required: true,
  },
  metric: {
    type: String as PropType<ChartMetric>,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  tooltipText: {
    type: String,
    required: true,
  },
  xLabel: {
    type: String,
    default: "",
  },
  yLabel: {
    type: String,
    default: "",
  },
  caption: {
    type: String,
    default: "",
  },
  chartId: {
    type: Number,
    required: true,
  },
  chartSize: {
    type: Number,
    required: true,
  },
  showContextMenu: {
    type: Boolean,
    default: true,
  },
  isChartEditable: {
    type: Boolean,
    default: true,
  },
  series: {
    type: Object as PropType<Object | null>,
    default: null,
  },
  hideLegend: {
    type: Boolean,
    default: false,
  },
  isCurrency: {
    type: Boolean,
    default: false,
  },
  dataText: {
    type: String,
    default: null,
  },
  showChartWrapper: {
    type: Boolean,
    default: true,
  },
  showChartAnalyticsWrapper: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: true,
  },
  position: {
    type: Number,
    default: 0,
  },
});

const isLoadingState = computed(() => {
  const isLoading = chartsStore.isLoading;

  const hasSeriesData =
    seriesData.value !== null && seriesData.value !== undefined;

  return isLoading || !hasSeriesData;
});

const wrapperComponent = computed(() => {
  return props.showChartAnalyticsWrapper
    ? ChartAnalyticsWrapper
    : props.showChartWrapper
      ? ChartWrapper
      : "div";
});

const seriesData = computed(() => {
  let series;

  if (props.series) {
    series = props.series;
  } else {
    series = processSeries(
      props.metric,
      props.type,
      props.sliceBy,
      props.title,
      chartsStore.dashboardChartsData,
      savedSearchStore.nestedSearches,
      savedSearchStore.selectedNestedSearch.id ?? "-1",
    );
  }

  // Translate system labels (like sentiment) while keeping user data (like keywords) as-is
  return translateSeriesNames(series, t);
});

const defaultColors = [
  "#2D556E",
  "#3B82F6",
  "#0EA5E9",
  "#06B6D4",
  "#14B8A6",
  "#10B981",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#6B7280",
  "#84CC16",
  "#D946EF",
  "#EC4899",
  "#F43F5E",
  "#EAB308",
  "#F59E0B",
  "#F97316",
  "#EF4444",
];

const chartOptions = computed(() => {
  let options = {
    title: {
      text: null,
    },

    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },

    credits: {
      enabled: false,
    },

    // assign colors based on length of data we're getting
    colors: defaultColors.slice(0, seriesData.value.length),

    chart: {
      events: {
        load() {
          emit("chartLoaded");
        },
      },
    },
    series: seriesData.value,
    lang: {
      noData: t("charts.noDataAvailable"),
    },
    noData: {
      style: {
        fontFamily: "Inter",
        fontWeight: "400",
      },
    },

    exporting: {
      enabled: true,
      filename: `${savedSearchStore.selectedSearch.name} - ${props.title}`,
      chartOptions: {
        title: {
          text: `${props.title}`,
          align: "left",
          style: {
            color: "#27272A",
            fontSize: "1rem",
            fontWeight: "400",
            fontFamily: '"Inter", sans-serif',
          },
        },
      },
      fonts: [
        {
          family: "Inter",
          weight: "400",
          source:
            "https://fonts.gstatic.com/s/Inter/v23/S6uyw4BMUTPHvxk6XweuBCY.woff2",
        },
      ],
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
  };

  if (props.type === "line") {
    options = {
      ...options,
      ...lineChartOptions.value,
    };
  } else if (props.type === "pie") {
    // except pie charts. they need to be fabulous at all times.
    // not passing in default colors results in only 1 color for all slices
    options.colors = defaultColors;
    options = {
      ...options,
      ...pieChartOptions.value,
    };
  } else if (props.type === "column") {
    options = {
      ...options,
      ...columnChartOptions.value,
    };
  } else if (props.type === "bar") {
    options = {
      ...options,
      ...barChartOptions.value,
    };
  } else if (props.type === "packedbubble") {
    options = {
      ...options,
      ...packedBubbleChartOptions.value,
    };
  }
  return options;
});

const columnChartOptions = computed(() => {
  return {
    chart: {
      type: "column",
      events: {
        load() {
          emit("chartLoaded");
        },
      },
    },
    xAxis: {
      // Add any other non-datetime slice-by options here
      type: ["media_type"].includes(props.sliceBy) ? "category" : "datetime",
      title: {},
      dateTimeLabelFormats: ["media_type"].includes(props.sliceBy)
        ? undefined
        : {
            day: "%e %b",
            week: "%e %b",
            month: "%b '%y",
            year: "%Y",
          },
    },
    yAxis: {
      title: {
        text: props.yLabel,
      },
    },
    legend: {
      layout: "horizontal",
      align: "left",
      verticalAlign: "top",
    },
    plotOptions: {
      series: {
        stacking: "normal",
      },
    },
    tooltip: {
      pointFormatter(): string {
        const value = props.isCurrency ? `${convertCurrency(this.y)}` : this.y;
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${value}</b><br/>`;
      },
    },
  };
});

const barChartOptions = computed(() => {
  return {
    chart: {
      type: "bar",
      events: {
        load() {
          emit("chartLoaded");
        },
      },
    },
    xAxis: {
      // Add any other non-datetime slice-by options here
      type: ["media_type"].includes(props.sliceBy) ? "category" : "datetime",
      title: {},
      dateTimeLabelFormats: ["media_type"].includes(props.sliceBy)
        ? undefined
        : {
            day: "%e %b",
            week: "%e %b",
            month: "%b '%y",
            year: "%Y",
          },
    },
    yAxis: {
      title: {
        text: props.yLabel,
      },
    },
    legend: {
      layout: "horizontal",
      align: "left",
      verticalAlign: "top",
    },
    plotOptions: {
      series: {
        stacking: "normal",
        dataLabels: {
          enabled: false, // disable labels on bar graph. (hides weird bugs like 100% labels)
        },
      },
    },
    tooltip: {
      pointFormatter(): string {
        const value = props.isCurrency ? `${convertCurrency(this.y)}` : this.y;
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${value}</b><br/>`;
      },
    },
  };
});

const lineChartOptions = computed(() => {
  const options = {
    chart: {
      type: "line",
      events: {
        load() {
          emit("chartLoaded");
        },
      },
    },

    xAxis: {
      type: "datetime",
      title: {},
      dateTimeLabelFormats: {
        day: "%e %b",
        week: "%e %b",
        month: "%b '%y",
        year: "%Y",
      },
    },

    yAxis: {
      title: {
        text: props.yLabel,
      },
    },

    plotOptions: {
      series: {
        cursor: "pointer",
        events: {
          click: (event) => {
            emit("updateSearchText", event.point.series.name);
          },
        },
      },
      line: {
        lineWidth: 3,
        marker: {
          enabled: false,
        },
      },
    },
  };

  if (props.hideLegend) {
    options.legend = {
      enabled: false,
    };
  }

  return options;
});

const pieChartOptions = computed(() => {
  return {
    xAxis: {
      visible: false,
    },
    yAxis: {
      visible: false,
    },
    caption: {
      align: "center",
    },

    tooltip: {
      pointFormatter() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const point = this;
        const series = point.series;
        let total = 0;
        let tooltipStr = "";

        // Calculate the total value for the series
        series.points.forEach(function (p) {
          total += p.y;
        });

        // Iterate through the series points to display the data
        series.points.forEach(function (p) {
          const value = props.isCurrency ? `${convertCurrency(p.y)}` : p.y;
          const percentage = ((p.y / total) * 100).toFixed(2);
          tooltipStr += `<span style="color:${p.color}">\u25CF</span> ${p.name}: <b>${value}</b> (${percentage}%)<br/>`;
        });

        return tooltipStr;
      },
    },

    plotOptions: {
      pie: {
        startAngle: 90,
        showInLegend: true,
        dataLabels: {
          connectorColor: "black",
          allowOverlap: true,
        },
        borderRadius: 0,
      },
      series: {
        borderWidth: 0,
        colorByPoint: true,
        type: "pie",
        size: "100%",
        innerSize: "50%",
        dataLabels: {
          enabled: true,
          crop: false,
          distance: "10%",
          style: {
            fontWeight: "bold",
            fontSize: "16px",
          },
          connectorWidth: 1,
          formatter() {
            return this.point.isNull ? null : Math.round(this.percentage) + "%";
          },
        },
        states: {
          hover: {
            opacity: 1, // Set the desired opacity value
          },
        },
      },
    },
  };
});

const packedBubbleChartOptions = computed(() => {
  return {
    chart: {
      type: "packedbubble",
      events: {
        load() {
          emit("chartLoaded");
        },
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      pointFormat: `<b>{point.name}</b><br/>${t("common.value")}: {point.value}`,
    },
    plotOptions: {
      series: {
        cursor: "pointer",
        events: {
          click: (event) => {
            emit("updateSearchText", event.point.name);
          },
        },
      },
      packedbubble: {
        minSize: "50%",
        maxSize: "100%",
        zMin: 0,
        zMax: 1000,
        layoutAlgorithm: {
          splitSeries: false,
          gravitationalConstant: 0.02,
        },
        dataLabels: {
          enabled: true,
          style: {
            color: "black",
            textOutline: "none",
            fontWeight: "normal",
          },
          formatter() {
            const maxLength = 15;
            if (this.point.name.length > maxLength) {
              let wrappedText = "";
              const words = this.point.name.split(" ");
              let line = "";
              for (let i = 0; i < words.length; i++) {
                if ((line + words[i]).length > maxLength) {
                  wrappedText += line.trim() + "<br>";
                  line = "";
                }
                line += words[i] + " ";
              }
              wrappedText += line.trim() + "<br>";
              return wrappedText;
            } else {
              return this.point.name;
            }
          },
        },
      },
    },
  };
});

const handleEdit = (payload: AddChartPayload) => {
  // the emit from ChartEditor we recieve here should always be for Edit not Add
  payload.props.position = props.position; // including position so it doesnt get updated
  payload.callback = () => {
    // Redraw the chart after the edit happens
    // Yes, this will redraw all the charts
    // They love it!
    $bus.$emit("highcharts:redraw");
  };
  emit("handleEdit", payload);
  showChartEditor.value = false;
};

const toggleEditChart = () => {
  showChartEditor.value = !showChartEditor.value;
};

const downloadChartImage = () => {
  // TODO: Add TS support for HighCharts
  // TODO: We may need to add more options here
  myChart?.value?.chart?.exportChartLocal({
    type: "image/png",
    title: {
      text: `${props.title}`,
    },
    scale: 2,
  });
};

const removeChart = () => {
  removeAnalyticsChart(props.chartId.toString());
};

const downloadChartData = () => {
  // TODO: Add TS support for HighCharts
  // TODO: We may need to add more options here
  myChart?.value?.chart?.downloadCSV();
};

const highlightChartLine = (seriesName: string) => {
  myChart?.value?.chart?.series?.forEach((series) => {
    if (series.name === seriesName) {
      series.update({ opacity: 1 }, false);
    } else {
      series.update({ opacity: 0.2 }, false);
    }
  });
  redraw();
};

const resetChartLine = () => {
  myChart?.value?.chart?.series?.forEach((series) => {
    series.update({ opacity: 1 }, false);
  });
  redraw();
};

const highlightChartBubble = (seriesName: string) => {
  myChart?.value?.chart?.series?.forEach((series) => {
    series.points.forEach((point) => {
      if (series.name !== seriesName) {
        point.update(
          { color: chartOptions.value.colors[point.colorIndex] + "11" },
          false,
        );
      }
    });
  });
};

const resetChartBubble = () => {
  myChart?.value?.chart?.series?.forEach((serie) => {
    serie.points.forEach((point) => {
      point.update(
        { color: chartOptions.value.colors[point.colorIndex] },
        false,
      );
    });
  });
  redraw();
};

const redraw = () => {
  chartKey.value += 1;
};

onMounted(() => {
  $bus.$off("highcharts:redraw", redraw);
  $bus.$on("highcharts:redraw", redraw);
});

defineExpose({
  highlightChartLine,
  highlightChartBubble,
  resetChartLine,
  resetChartBubble,
  redraw,
});
</script>

<style>
@import url("https://fonts.googleapis.com/css?family=Inter:300,400,700&display=swap");
.content-popover {
  @apply p-0 !important;
  font-family: Inter, sans-serif;
}
.context-menu-option {
  @apply w-full flex items-center gap-2 cursor-pointer hover:bg-secondary-50 p-4 text-slate-900 rounded font-body-2;
}
.custom-button {
  @apply bg-transparent border-none hover:border-none active:bg-transparent focus:bg-transparent hover:bg-transparent p-0;
}
</style>
